import React from 'react';
import { Stack, Typography, IconButton, Tooltip } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { useCopy } from 'new/hooks/useCopy';
import styles from './CopyableText.module.scss';

interface CopyableTextProps {
  /**
   * The text value to display and copy
   */
  value: string;
  /**
   * Optional label to display above the value
   */
  label?: string;
  /**
   * Unique identifier for tracking copy state
   */
  fieldId?: string;
  /**
   * Whether to truncate the value with ellipsis
   */
  truncate?: boolean;
  /**
   * Maximum width for the value (when truncate is true)
   */
  maxWidth?: number | string;
  /**
   * Typography variant for the value
   */
  variant?: 'body1' | 'body2' | 'caption';
  /**
   * Whether to show the value in a monospace font
   */
  monospace?: boolean;
}

const CopyableText: React.FC<CopyableTextProps> = ({
  value,
  label,
  fieldId,
  truncate = true,
  maxWidth = 280,
  variant = 'body2',
  monospace = true,
}) => {
  const { copy, isCopied } = useCopy();
  const id = fieldId || value;
  const copied = isCopied(id);

  const handleCopy = () => {
    copy(value, id);
  };

  return (
    <div className={styles.container}>
      {label && (
        <Typography variant="caption" color="text.secondary" className={styles.label}>
          {label}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5} className={styles.valueRow}>
        <Tooltip title={value} placement="top" arrow>
          <Typography
            variant={variant}
            className={`${styles.value} ${monospace ? styles.monospace : ''}`}
            sx={{
              ...(truncate && {
                maxWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }),
            }}
          >
            {value}
          </Typography>
        </Tooltip>
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            className={`${styles.copyButton} ${copied ? styles.copied : ''}`}
          >
            {copied ? (
              <CheckIcon fontSize="small" className={styles.checkIcon} />
            ) : (
              <CopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </div>
  );
};

export default CopyableText;
